<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("shopee_category_map.menu") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1">
                <template slot="header">
                    <div class="row align-items-center">
                        <div class="col-md-4">
                            <label class="form-control-label">{{ $t("shopee_category_map.shop") }}</label>
                            <el-select
                                v-model="shopId"
                                class="w-100"
                                :placeholder="$t('shopee_category_map.shop_placeholder')"
                                :no-data-text="$t('shopee_category_map.no_shop')"
                                @change="reload"
                            >
                                <el-option
                                    v-for="shop in shopeeShops"
                                    :key="shop.id"
                                    :label="shopLabel(shop)"
                                    :value="String(shop.shop_id)"
                                ></el-option>
                            </el-select>
                        </div>
                        <div class="col-md-8">
                            <p class="mb-0 text-muted small">{{ $t("shopee_category_map.hint") }}</p>
                            <p class="mb-0 text-muted small">
                                {{ $t("shopee_category_map.retroactive_hint") }}
                            </p>
                            <p class="mb-0 small">
                                <span class="text-muted">{{ $t("shopee_category_map.fetched_at") }}:</span>
                                {{ fetchedAtText }}
                            </p>
                        </div>
                    </div>
                </template>

                <div class="px-4 pb-3 d-flex flex-wrap">
                    <button
                        type="button"
                        class="btn btn-sm btn-success mr-2"
                        :disabled="saving || !hasChanges"
                        @click="save()"
                    >
                        <i class="fa fa-spinner fa-spin mr-1" v-if="saving"></i>
                        {{ $t("shopee_category_map.btn_save") }}
                    </button>
                    <button
                        type="button"
                        class="btn btn-sm btn-outline-primary mr-2"
                        :disabled="refreshing || !shopId"
                        @click="refreshNames()"
                    >
                        <i class="fa fa-spinner fa-spin mr-1" v-if="refreshing"></i>
                        {{ $t("shopee_category_map.btn_refresh") }}
                    </button>
                    <span class="text-muted small align-self-center" v-if="!hasChanges">
                        {{ $t("shopee_category_map.no_changes") }}
                    </span>
                </div>

                <div class="px-4 pb-3" v-if="errorKey">
                    <div class="alert alert-warning py-2 px-3 mb-0">
                        <i class="fa fa-exclamation-triangle mr-1"></i>
                        {{ $t(errorKey) }}
                    </div>
                </div>

                <div v-loading="loading">
                    <el-table
                        :data="rows"
                        row-key="shopee_category_id"
                        size="small"
                        header-row-class-name="thead-light"
                        :row-class-name="rowClassName"
                        :empty-text="$t('shopee_category_map.empty')"
                    >
                        <el-table-column
                            :label="$t('shopee_category_map.col_shopee_category')"
                            min-width="240"
                        >
                            <template slot-scope="{ row }">
                                {{ row.name_th || row.name_en || row.shopee_category_id }}
                                <span class="d-block text-muted small" v-if="row.path_th">
                                    {{ row.path_th }}
                                </span>
                                <span class="d-block text-muted small text-monospace">
                                    {{ row.shopee_category_id }}
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column
                            :label="$t('shopee_category_map.col_product_count')"
                            width="130"
                            align="right"
                            prop="product_count"
                        >
                        </el-table-column>
                        <el-table-column
                            :label="$t('shopee_category_map.col_seller_category')"
                            min-width="260"
                        >
                            <template slot-scope="{ row }">
                                <el-select
                                    v-model="row.category_id"
                                    filterable
                                    size="mini"
                                    class="w-100"
                                    :placeholder="$t('shopee_category_map.auto_placeholder')"
                                >
                                    <!-- value 0 = "ให้สร้างอัตโนมัติ": §6D.4 spells the
                                         DELETE of a map row as category_id=0, so the
                                         "no mapping" choice has to be a real option
                                         with a real value rather than an empty
                                         selection the diff cannot see. -->
                                    <el-option
                                        :label="$t('shopee_category_map.auto_option')"
                                        :value="autoValue"
                                    ></el-option>
                                    <el-option
                                        v-for="cat in categories"
                                        :key="cat.id"
                                        :label="cat.name_th || cat.name"
                                        :value="cat.id"
                                    ></el-option>
                                </el-select>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('shopee_category_map.col_source')" width="130">
                            <template slot-scope="{ row }">
                                <el-tag size="mini" :type="sourceTagType(row)">
                                    {{ $t(sourceKey(row)) }}
                                </el-tag>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
            </card>
        </div>
    </div>
</template>

<script>
// #282 — "จับคู่หมวด Shopee → seller" (spec §6D.6, endpoints §6D.4).
//
// WHAT THIS PAGE IS FOR: since §6D a Shopee sync no longer asks the operator to
// pick a seller category. An unmapped Shopee global category CREATES a seller
// category with the same name (§6D.1 rule 1), which is right often enough to be
// worth automating and wrong often enough to need a place to correct it — this
// page. Pointing Shopee's 100661 at the existing "น้ำหอม" here is what stops a
// second, near-duplicate category appearing on the next sync.
//
// WHAT IT DELIBERATELY DOES NOT DO: it does not move products. §6D.1 rule 5 is
// explicit that there is no retroactive re-categorisation, and the page says so
// in words (retroactive_hint) rather than letting the operator assume a save
// tidies up what has already been synced.
//
// THE SAVE SENDS ONLY WHAT CHANGED. categoryMapDiff is what the PUT is built
// from — sending the whole table would stamp source='manual' on every row the
// operator never touched, freezing rows that should keep auto-resolving.
//
// The page is modelled on PlatformCategoryMap.vue (#263), including the
// warning tint on the rows that still have no mapping.
import { apiErrorMessage } from "@/util/api-error";
import { mapGetters, mapActions } from "vuex";
import { Select, Option, Table, TableColumn, Tag, Loading } from "element-ui";
import swal from "sweetalert2";
import { ShopeeService } from "@/resource";
import { SHOPEE_PLATFORM_ID } from "@/utils/shopeeToSeller";
import {
    AUTO_CATEGORY_VALUE,
    categoryMapRows,
    categoryMapDiff
} from "@/utils/shopeeCategoryMap";

export default {
    name: "AppCatalogShopeeCategoryMap",
    components: {
        // Element UI is NOT registered globally in this app (only Input,
        // Tooltip and Popover are — src/plugins/globalComponents.js), so every
        // el-* used above has to be registered here.
        [Select.name]: Select,
        [Option.name]: Option,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag
    },
    directives: {
        // v-loading is a DIRECTIVE and is not installed globally either.
        loading: Loading.directive
    },
    data() {
        return {
            shopId: "",
            loading: false,
            saving: false,
            refreshing: false,
            errorKey: "",
            fetchedAt: "",
            // `rows` is what the selects bind to; `original` is the untouched
            // snapshot the diff is taken against. They must be separate objects,
            // not two references to the same array, or every row would read as
            // unchanged.
            rows: [],
            original: []
        };
    },
    computed: {
        ...mapGetters({
            categories: "AppCatalogCategory/list",
            sellers: "Platform/getSellers"
        }),
        autoValue() {
            return AUTO_CATEGORY_VALUE;
        },
        // Shopee sellers only — the map is keyed by Shopee's global category
        // ids, which no other platform has (same platform_id === 3 filter
        // /admin/product_platform uses for its shop select).
        shopeeShops() {
            const list = this.sellers || [];
            return list.filter(function(shop) {
                return Number(shop.platform_id) === SHOPEE_PLATFORM_ID;
            });
        },
        changedItems() {
            return categoryMapDiff(this.original, this.rows);
        },
        hasChanges() {
            return this.changedItems.length > 0;
        },
        fetchedAtText() {
            return this.fetchedAt || this.$t("shopee_category_map.fetched_at_never");
        }
    },
    mounted() {
        const self = this;
        this.fetchCategories({});
        this.listSeller().then(function() {
            self.selectFirstShop();
        });
    },
    methods: {
        ...mapActions({
            fetchCategories: "AppCatalogCategory/fetchList",
            listSeller: "Platform/listSeller"
        }),
        shopLabel(shop) {
            const name = (shop && shop.seller_name) || "";
            const id = (shop && shop.shop_id) || "";
            return name ? name + " (" + id + ")" : String(id);
        },
        selectFirstShop() {
            const shops = this.shopeeShops;
            if (!shops.length) {
                this.shopId = "";
                return;
            }
            this.shopId = String(shops[0].shop_id);
            this.reload();
        },
        // Rows with no mapping yet are the whole point of the page, so they are
        // tinted rather than only badged (the same move PlatformCategoryMap
        // makes for its unmapped seller categories).
        rowClassName({ row }) {
            return row && row.source ? "" : "shopee-category-unmapped";
        },
        sourceKey(row) {
            const source = (row && row.source) || "";
            if (source === "auto" || source === "manual") {
                return "shopee_category_map.source." + source;
            }
            return "shopee_category_map.source.none";
        },
        sourceTagType(row) {
            const source = (row && row.source) || "";
            if (source === "manual") return "success";
            if (source === "auto") return "info";
            return "warning";
        },
        async reload() {
            if (!this.shopId) {
                this.rows = [];
                this.original = [];
                this.fetchedAt = "";
                return;
            }
            this.loading = true;
            this.errorKey = "";
            try {
                const res = await ShopeeService.CategoryMapList({
                    platform_shop_id: String(this.shopId)
                });
                const data = (res && res.data) || {};
                const rows = categoryMapRows(data.rows);
                this.rows = rows;
                // A SECOND normalisation, not a slice of the first: the selects
                // mutate `rows` in place, and a shared object graph would make
                // the diff always empty.
                this.original = categoryMapRows(data.rows);
                this.fetchedAt = data.fetched_at || "";
            } catch (err) {
                this.rows = [];
                this.original = [];
                this.$notify({ type: "danger", message: apiErrorMessage(err, this.$t) });
            } finally {
                this.loading = false;
            }
        },
        async save() {
            const items = this.changedItems;
            if (!items.length) return;
            this.saving = true;
            this.errorKey = "";
            try {
                await ShopeeService.CategoryMapPut({ items: items });
                // Element UI 2.4.11 has no MessageBox in this app; sweetalert2
                // is the dialog every other Admin screen uses.
                await swal.fire({
                    title: this.$t("shopee_category_map.saved"),
                    text: this.$t("shopee_category_map.saved_text", { count: items.length }),
                    icon: "success",
                    confirmButtonClass: "btn btn-success btn-fill",
                    buttonsStyling: false
                });
                await this.reload();
            } catch (err) {
                this.errorKey = this.errorI18nKey(err);
                this.$notify({ type: "danger", message: apiErrorMessage(err, this.$t) });
            } finally {
                this.saving = false;
            }
        },
        async refreshNames() {
            if (!this.shopId) return;
            this.refreshing = true;
            this.errorKey = "";
            try {
                const res = await ShopeeService.CategoryMapRefresh({
                    platform_shop_id: String(this.shopId)
                });
                const data = (res && res.data) || {};
                this.$notify({
                    type: "success",
                    message: this.$t("shopee_category_map.refreshed_text", {
                        count: Number(data.count) || 0
                    })
                });
                await this.reload();
            } catch (err) {
                this.errorKey = this.errorI18nKey(err);
                this.$notify({ type: "danger", message: apiErrorMessage(err, this.$t) });
            } finally {
                this.refreshing = false;
            }
        },
        // 400 bad_category and 502 are the two answers with an actionable
        // sentence of their own (§6D.4); everything else is generic.
        errorI18nKey(err) {
            const response = (err && err.response) || {};
            const data = response.data || {};
            const code = data.code || data.Code || data.error_code || "";
            if (String(code) === "bad_category") return "shopee_category_map.error.bad_category";
            if (String(code) === "shop_required") return "shopee_category_map.error.shop_required";
            if (Number(response.status) === 502) return "shopee_category_map.error.upstream";
            return "shopee_category_map.error.generic";
        }
    }
};
</script>

<style>
/* A Shopee category with no map row will auto-create a seller category on the
   next sync — the row most worth reading, so it is tinted. */
.el-table .shopee-category-unmapped {
    background-color: #fff8e1;
}
</style>
